'use client';

import { EnvelopeSimpleIcon, MapPinLineIcon, PhoneIcon } from '@phosphor-icons/react/dist/ssr';
import Link from 'next/link';
import { BrandLogo } from '@/components/layouts/brand-logo';
import { CONTACT } from '@/features/home/data/home-content';
import { NAV_LINKS } from '@/components/common/nav-content';
import { EmailReveal } from '@/components/common/email-reveal';
import { PhoneReveal } from '@/components/common/phone-reveal';
import { useSuburbsList } from '@/features/suburb/api/suburbs.queries';

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" stroke="#35B728" />
      <path
        d="M13.3346 12.9997H15.0013L15.668 10.333H13.3346V8.99967C13.3346 8.31301 13.3346 7.66634 14.668 7.66634H15.668V5.42634C15.4506 5.39767 14.63 5.33301 13.7633 5.33301C11.9533 5.33301 10.668 6.43767 10.668 8.46634V10.333H8.66797V12.9997H10.668V18.6663H13.3346V12.9997Z"
        fill="#35B728"
      />
    </svg>
  );
}

function LinkedinIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" stroke="#35B728" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.3409 9.864V10.9953C12.5778 10.6345 12.9044 10.3413 13.2886 10.1445C13.6728 9.94768 14.1016 9.85402 14.5329 9.87267C16.8362 9.87267 17.3342 11.3127 17.3342 13.186V17H15.2009V13.6187C15.2009 12.812 15.0382 11.7747 13.7822 11.7747C12.5642 11.7747 12.3562 12.6527 12.3562 13.5587V17H10.2296V9.864H12.3409ZM8.80089 8.07067C8.80063 8.282 8.73798 8.48856 8.62081 8.66444C8.50364 8.84031 8.33715 8.97769 8.14222 9.05933C7.94738 9.14032 7.73284 9.16151 7.52591 9.12019C7.31899 9.07888 7.12903 8.97693 6.98022 8.82733C6.83113 8.67764 6.72963 8.4872 6.68848 8.27998C6.64734 8.07275 6.66838 7.85798 6.74896 7.66268C6.82955 7.46738 6.96608 7.30027 7.14139 7.18236C7.3167 7.06445 7.52295 7.001 7.73422 7C7.87464 7 8.01367 7.02772 8.14335 7.08158C8.27302 7.13543 8.39079 7.21436 8.48989 7.31383C8.58899 7.41331 8.66748 7.53137 8.72085 7.66124C8.77422 7.79112 8.80142 7.93025 8.80089 8.07067Z"
        fill="#35B728"
      />
      <path d="M8.8013 9.87305H6.66797V17.0004H8.8013V9.87305Z" fill="#35B728" />
    </svg>
  );
}

const SOCIAL_LINKS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/dc.commercial.qld',
    Icon: FacebookIcon,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/dccommercial-qld',
    Icon: LinkedinIcon,
  },
] as const;

const FOOTER_HEADING =
  'Specialising in commercial real estate throughout Queensland, DC Commercial delivers an experience and service like no other. Independently owned and operated, the boutique commercial agency is dedicated to providing an honest and efficient approach when attending to the needs of your commercial property.';

export function SiteFooter({ description }: { description?: string }) {
  const { data: suburbsData } = useSuburbsList({
    isActive: true,
    limit: 100,
    sortBy: 'displayOrder',
    sortOrder: 'ASC',
  });

  const dynamicNavLinks = NAV_LINKS.map((link) => {
    if (link.label === 'Suburbs') {
      const firstSuburb = suburbsData?.items?.[0];

      return {
        ...link,
        href: firstSuburb ? `/suburbs/${firstSuburb.slug}` : '#',
        children: undefined,
        disabled: !firstSuburb,
      };
    }
    return {
      ...link,
      children: undefined,
    };
  });

  return (
    <footer
      id="contact"
      className="bg-[var(--primary-black)] text-[#b3b3b3] px-4 pt-15 pb-0 sm:px-6 lg:px-8"
    >
      <div className="mx-auto flex flex-wrap justify-between max-w-7xl gap-10 pb-7.5">
        <div className="flex flex-col gap-4 lg:w-[360px]">
          <BrandLogo variant="light" />
          <p className="text-sm">{description ?? FOOTER_HEADING}</p>
        </div>

        <nav
          aria-label="Footer navigation"
          className="flex flex-1 flex-col gap-2 min-w-[200px] max-w-[200px]"
        >
          <p className="mb-2 text-xs font-semibold tracking-[0.18em] text-white uppercase">
            Navigate to
          </p>
          {dynamicNavLinks.map((link) => (
            <div key={link.href} className="flex flex-col gap-2">
              {link.disabled || link.href === '#' ? (
                <span
                  title={link.disabled ? 'No suburbs available currently' : undefined}
                  className={`text-sm ${
                    link.disabled
                      ? 'cursor-not-allowed text-white/40'
                      : 'cursor-default transition hover:text-white'
                  }`}
                >
                  {link.label}
                </span>
              ) : (
                <Link
                  href={link.href}
                  prefetch={false}
                  className="text-sm transition hover:text-white"
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}
        </nav>

        <div className="flex flex-1 flex-col gap-2 max-w-[100px]">
          <p className="mb-2 text-xs font-semibold tracking-[0.18em] text-white uppercase">
            Follow us
          </p>
          <ul className="flex flex-col gap-4">
            {SOCIAL_LINKS.map(({ label, href, Icon }) => (
              <li key={label}>
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 text-sm transition hover:text-white"
                >
                  <Icon className="h-6 w-6 text-[var(--user-green)]" />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-1 flex-col gap-2 min-w-[300px] max-w-[300px]">
          <p className="mb-2 text-xs font-semibold tracking-[0.18em] text-white uppercase">
            Contact us
          </p>
          <ul className="flex flex-col gap-4 text-sm">
            <li className="flex items-center gap-3">
              <MapPinLineIcon className="h-4 w-4 text-white shrink-0" />
              <span>{CONTACT.address}</span>
            </li>
            <li className="flex items-center gap-3 text-[var(--user-green)]">
              <EnvelopeSimpleIcon className="h-4 w-4 shrink-0" />
              <EmailReveal
                email={CONTACT.email}
                hiddenClassName="cursor-pointer text-sm underline transition hover:text-white"
                shownClassName="text-sm transition hover:text-white"
              />
            </li>
            <li className="flex items-center gap-3 text-white">
              <PhoneIcon className="h-4 w-4 shrink-0" />
              <PhoneReveal
                phone={CONTACT.phone}
                phoneHref={CONTACT.phoneHref}
                hiddenClassName="cursor-pointer underline text-[var(--user-green)] transition hover:text-white"
                shownClassName="transition hover:text-white"
              />
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="text-[#909090] mx-auto flex flex-wrap max-w-7xl items-center lg:justify-between justify-center gap-3 pt-7.5 pb-10 text-xs">
          <p className="lg:text-left text-center">
            © DC Commercial — Toowoomba, QLD. Commercial sales, leasing & property management. All
            Rights Reserved.
          </p>
          <ul className="flex items-center divide-x divide-white/20">
            <li className="pr-5">
              <Link
                href="/terms-conditions"
                prefetch={false}
                className="transition hover:text-white"
              >
                Terms &amp; Conditions
              </Link>
            </li>
            <li className="pl-5">
              <Link href="/privacy" prefetch={false} className="transition hover:text-white">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
